import { Check } from "lucide-react";
import { cn } from "@wa/ui";

/**
 * One vocabulary drawn as pills, in the three states a grant can be in.
 *
 * The same control now decides three different things: which portal modules the
 * CRM-wide default opens, which ones one company gets, and which ones one person
 * at that company keeps. So it lives here rather than inside any one of them. It
 * is purely presentational - it holds no state, saves nothing, and knows nothing
 * about which level it is editing. WHEN the value is written (per tick on a
 * contact, deferred behind a Save on the client and the global default) is the
 * caller's decision and deliberately not one this file can make for them: a
 * family edit is several ticks, and each intermediate state saved would be a
 * real access change with its own audit row.
 *
 * ── THE FIRST CLICK OUT OF `null` SUBTRACTS ────────────────────────────────
 *
 * `null` is "this level decided nothing", which draws as every pill the level
 * above allows switched on. The first click on a pill therefore means
 * "everything except this one", never "only this one". The operator is looking
 * at a row of ticks and taking one away; the other reading would turn one click
 * into four closures nobody asked for.
 *
 * ── WHICH IS WHY `baseWhenNull` EXISTS ─────────────────────────────────────
 *
 * That first click starts from what the level ABOVE allows, not from the whole
 * vocabulary. Starting from the vocabulary at a company that had two modules
 * closed would write those two into the new array, and since the array is then
 * the level's own opinion rather than an inherited blank, the closure would be
 * gone with no screen having said so. The caller passes the resolved parent set.
 * The default is `items`, which is only correct where there is no parent - the
 * global default is the one caller that may leave it out.
 *
 * ── `unavailable` AND `pinned` ARE DRAWN, NOT ENFORCED ─────────────────────
 *
 * A pill the parent closed is struck through and dead; a pill that cannot be
 * switched off is ticked and dead. Both are statements about a decision made on
 * another screen, so the reason the pill will not move has to be visible on the
 * pill itself. A control that silently swallowed the click would read as broken,
 * and the operator's next move would be to click it harder.
 */
export interface GrantPillsProps<T extends string> {
  /** The whole vocabulary, in the order it is drawn and stored. */
  items: readonly T[];
  /** This level's stored grant. `null` = inherited, `[]` = nothing. */
  grant: T[] | null;
  /** Called with the level's new grant. `null` puts it back to inherited. */
  onChange: (next: T[] | null) => void;
  labelFor: (item: T) => string;
  /** Closed by the level ABOVE: struck through and disabled. */
  unavailable?: readonly T[];
  /** Cannot be switched off: ticked and disabled. */
  pinned?: readonly T[];
  /** The "back to inherited" pill, shown only while `grant` is an array. */
  clearLabel: string;
  disabled?: boolean;
  /** The set the first untick starts from when the grant is null. Defaults to `items`. */
  baseWhenNull?: readonly T[];
}

export function GrantPills<T extends string>({
  items,
  grant,
  onChange,
  labelFor,
  unavailable,
  pinned,
  clearLabel,
  disabled = false,
  baseWhenNull,
}: GrantPillsProps<T>) {
  const base = baseWhenNull ?? items;
  const closed = new Set<T>(unavailable ?? []);
  const fixed = new Set<T>(pinned ?? []);

  /**
   * What the pill shows, which is the EFFECTIVE answer and not the stored one.
   *
   * A stored item the parent has since closed is drawn off, because off is what
   * the person on the other end of the portal experiences. The stored value is
   * left alone all the same (see `toggle`), so the two can disagree here on
   * purpose: the strike-through is what tells the operator the difference exists.
   */
  function isOn(item: T): boolean {
    if (fixed.has(item)) return true;
    if (closed.has(item)) return false;
    return grant === null ? base.includes(item) : grant.includes(item);
  }

  function toggle(item: T) {
    // From `null` this starts at the parent's set, so the click subtracts one
    // rather than re-opening whatever the parent closed. From an array it starts
    // at the array itself, INCLUDING any entry the parent has closed since it was
    // saved: dropping those would quietly destroy an operator's stored intent the
    // moment somebody edits an unrelated pill, and the closure lower down is the
    // gate's job anyway, not this list's.
    const next = new Set<T>(grant === null ? base : grant);
    if (next.has(item)) next.delete(item);
    else next.add(item);
    // A pinned entry is written out even when the base it came from omitted it.
    // The alternative is a saved array that contradicts a pill this component
    // draws permanently on, and a stored value that disagrees with the screen is
    // the thing every reader of that value has to be warned about forever.
    for (const p of fixed) next.add(p);
    onChange(items.filter((i) => next.has(i)));
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => {
        const on = isOn(item);
        const locked = closed.has(item) || fixed.has(item);
        return (
          <button
            key={item}
            type="button"
            onClick={() => toggle(item)}
            disabled={disabled || locked}
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-60",
              on
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:bg-accent",
              closed.has(item) && "line-through",
              locked && "cursor-default",
            )}
          >
            {on && <Check className="size-3" />}
            {labelFor(item)}
          </button>
        );
      })}
      {grant !== null && (
        <button
          type="button"
          onClick={() => onChange(null)}
          disabled={disabled}
          className="rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-accent disabled:opacity-60"
        >
          {clearLabel}
        </button>
      )}
    </div>
  );
}
